// ─────────────────────────────────────────────────────────────────────────────
// Clinic money in more than one currency — the doctor panel's grouping rules.
//
// A doctor can work at clinics that keep their books in different currencies
// (docs/clinic-currency/CONTRACTS.md). Every clinic-money row the BFF returns says
// which currency its amounts are minor units of, and the rules for showing them
// are the kit's (`@parsina/framework/ui/lib/currency`). What this module adds is
// the one thing a screen needs on top: never let two currencies meet.
//
//   • a total is one figure PER CURRENCY — there is no exchange rate anywhere,
//     so «1,500,000 ت + 12.50 AED» is not a number anybody can be paid;
//   • a bar chart scales against the largest bar WITHIN its currency, because a
//     bar measured against another currency's row compares magnitudes that have
//     nothing to do with each other;
//   • an empty report has no rows to read a currency from, so its zero is stated
//     in the doctor's clinic's currency (`displayCurrencyFor`), IRR when unknown.
//
// For an IRR-only doctor — every doctor today — each function here yields one
// IRR group, and the screens render exactly what they rendered before.
// ─────────────────────────────────────────────────────────────────────────────

import { DEFAULT_CURRENCY, normalizeCurrency } from '@parsina/framework/ui/lib/currency';

export { DEFAULT_CURRENCY };

/**
 * A row's currency: blank or absent reads IRR (an older server, a lagging proto
 * copy). A code outside the allow-list THROWS rather than rendering as IRR — the
 * kit's rule, since a wrong unit on money is worse than a failed render.
 */
export const currencyOf = (row) => normalizeCurrency(row?.currency);

export const isIrr = (currency) => normalizeCurrency(currency) === DEFAULT_CURRENCY;

/**
 * Rows split by currency, in the order the currencies first appear:
 * `[{ currency, rows }]`. The BFF already sends IRR first, so an IRR-only list
 * comes back as one group holding every row, in its original order.
 */
export function groupByCurrency(rows) {
  const groups = [];
  const byCode = new Map();
  for (const row of rows || []) {
    const code = currencyOf(row);
    let group = byCode.get(code);
    if (!group) {
      group = { currency: code, rows: [] };
      byCode.set(code, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}

/**
 * The value a bar chart scales against: the largest positive `pick(row)` among
 * rows that share ONE currency — never across currencies. 1 when nothing is
 * positive, so an all-zero chart draws empty bars instead of dividing by zero.
 * (Equal to the first row's value for a list sorted by it, which the clinic
 * breakdown is — so an IRR-only chart draws the same widths as before.)
 */
export function scaleMax(rows, pick = (row) => row.grossTotal) {
  let max = 0;
  for (const row of rows || []) {
    const value = Number(pick(row));
    if (Number.isFinite(value) && value > max) max = value;
  }
  return max || 1;
}

/** `outstanding` is per currency by construction: both terms come from one group. */
const withOutstanding = (group) => ({
  ...group,
  outstanding: group.doctorShareTotal - group.collectedDoctorShare,
});

/**
 * The accounting report's totals, one entry per currency:
 * `[{ currency, grossTotal, doctorShareTotal, collectedDoctorShare, outstanding }]`.
 *
 * Read from `totalsByCurrency`, never from the scalar totals when there is more
 * than one currency — the BFF sends those as null then, and `null || 0` would
 * show a doctor a confident zero. An EMPTY report has no currency at all: it gets
 * one zero entry in `fallbackCurrency` (the clinic's, see `displayCurrencyFor`).
 */
export function reportTotals(report, fallbackCurrency = DEFAULT_CURRENCY) {
  const list = Array.isArray(report?.totalsByCurrency) ? report.totalsByCurrency : [];
  if (list.length) {
    return list.map((group) => withOutstanding({
      currency: currencyOf(group),
      grossTotal: group.grossTotal ?? 0,
      doctorShareTotal: group.doctorShareTotal ?? 0,
      collectedDoctorShare: group.collectedDoctorShare ?? 0,
    }));
  }
  return [withOutstanding({
    currency: normalizeCurrency(fallbackCurrency),
    grossTotal: report?.grossTotal ?? 0,
    doctorShareTotal: report?.doctorShareTotal ?? 0,
    collectedDoctorShare: report?.collectedDoctorShare ?? 0,
  })];
}

/**
 * The currency to state a figure in when there is no row to read one from — an
 * empty report, a day with no invoiced work.
 *
 *   1. the selected clinic's, when a clinic is selected and known;
 *   2. otherwise the one currency every clinic of the doctor's shares;
 *   3. otherwise IRR — including while the clinic list is still loading, which
 *      is also what every one of those clinics was before currencies existed.
 *
 * `clinics` are `{ id, currency }` rows (the clinic switch's targets).
 */
export function displayCurrencyFor(clinics, selectedId) {
  const list = clinics || [];
  if (selectedId) {
    const selected = list.find((clinic) => clinic?.id === selectedId);
    if (selected) return currencyOf(selected);
  }
  const codes = new Set(list.map(currencyOf));
  return codes.size === 1 ? [...codes][0] : DEFAULT_CURRENCY;
}

/**
 * The income card's figures, one entry per currency: `[{ currency, shareRial,
 * unsettledRial }]` (useReception's `toTodayIncome` shape). A day with no
 * invoiced line has no currency of its own, so it becomes ONE zero entry in
 * `fallbackCurrency` — exactly the «۰ تومان» an IRR doctor has always seen.
 */
export function incomeGroups(income, fallbackCurrency = DEFAULT_CURRENCY) {
  if (Array.isArray(income?.byCurrency) && income.byCurrency.length) {
    return income.byCurrency.map((group) => ({ ...group, currency: currencyOf(group) }));
  }
  return [{
    currency: normalizeCurrency(fallbackCurrency),
    shareRial: income?.shareRial ?? 0,
    unsettledRial: income?.unsettledRial ?? 0,
  }];
}
